import React, { useEffect, useRef, useState } from "react";
import "./AddToMailingListModal.css";

// Mailing List add-by-search. Same shape as the Incentives "Add"
// modal and ReassignOwnerModal, narrowed to "find a socialUsers row
// and flip emailApproved=true on it". The mutation server-side also
// clears any leftover emailUnsubscribed flag so an old /unsubscribe
// click doesn't quietly keep the user excluded after re-adding.
//
// Search is debounced so a fast typist doesn't fire one query per
// keystroke. Caller passes:
//   - executeQuery / executeMutation hooks (already configured with
//     auth headers in the surrounding context)
//   - onAdded(updatedSocialUser) called once the mutation lands so
//     the parent can refresh its list.

const SEARCH_USERS = `
  query SearchSocialUsersForMailing($query: String!) {
    searchSocialUsers(query: $query) {
      userId
      email
      firstName
      lastName
      name
      joinDate
      approved
      paid
    }
  }
`;

const ADD_TO_LIST = `
  mutation AddUserToMailingList($userId: ID!) {
    addUserToMailingList(userId: $userId) {
      userId
      email
      firstName
      lastName
      name
      joinDate
    }
  }
`;

const SEARCH_DEBOUNCE_MS = 300;

const AddToMailingListModal = ({
  onClose,
  onAdded,
  executeQuery,
  executeMutation,
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submittingId, setSubmittingId] = useState("");
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 50);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const data = await executeQuery(SEARCH_USERS, { query: q });
        setResults(data?.searchSocialUsers || []);
      } catch (err) {
        setError(err.message || "Search failed");
      } finally {
        setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, executeQuery]);

  const handleAdd = async (u) => {
    if (!u.userId) return;
    setSubmittingId(u.userId);
    setError("");
    try {
      const data = await executeMutation(ADD_TO_LIST, { userId: u.userId });
      const updated = data?.addUserToMailingList;
      if (updated) onAdded(updated);
    } catch (err) {
      setError(err.message || "Add failed");
    } finally {
      setSubmittingId("");
    }
  };

  return (
    <div
      className="add-mail__backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="add-mail__panel"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="add-mail__header">
          <h3 className="add-mail__title">Add user to mailing list</h3>
          <button
            type="button"
            className="add-mail__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <p className="add-mail__hint">
          Search socialUsers; tap "Add" to flip <code>emailApproved=true</code>{" "}
          on their row and clear any old unsubscribe marker.
        </p>
        <input
          ref={inputRef}
          type="search"
          className="add-mail__search"
          placeholder="Search by first or last name, email…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        {query.trim().length > 0 && query.trim().length < 2 && (
          <div className="add-mail__info">Type at least 2 characters…</div>
        )}
        {loading && <div className="add-mail__info">Searching…</div>}
        {error && <div className="add-mail__error">{error}</div>}
        {!loading &&
          !error &&
          query.trim().length >= 2 &&
          results.length === 0 && (
            <div className="add-mail__info">
              No matches for "{query.trim()}".
            </div>
          )}

        <div className="add-mail__results">
          {results.map((u) => {
            const name =
              [u.firstName, u.lastName].filter(Boolean).join(" ") ||
              u.name ||
              u.email ||
              u.userId;
            const isBusy = submittingId === u.userId;
            return (
              <div key={u.userId} className="add-mail__row">
                <div className="add-mail__row-main">
                  <div className="add-mail__row-name">{name}</div>
                  <div className="add-mail__row-meta">
                    <span>{u.email || "(no email)"}</span>
                    {u.joinDate && <span>· joined {u.joinDate}</span>}
                    {typeof u.paid === "boolean" && (
                      <span>· {u.paid ? "paid" : "unpaid"}</span>
                    )}
                    {typeof u.approved === "boolean" && (
                      <span>· {u.approved ? "approved" : "not approved"}</span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className="add-mail__btn add-mail__btn--primary"
                  onClick={() => handleAdd(u)}
                  disabled={!u.email || isBusy}
                  title={
                    u.email
                      ? "Add to mailing list"
                      : "No email — can't be on the mailing list"
                  }
                >
                  {isBusy ? "Adding…" : "Add"}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AddToMailingListModal;
